import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  addEdge, Background, MarkerType, MiniMap, Panel, ReactFlow, ReactFlowProvider, useEdgesState, useNodesState, useReactFlow, useViewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Download, Map as MapIcon, Maximize, Maximize2, Minimize2, Minus, Moon, Play, Plus, Rocket, Sun, Trash2, Upload } from 'lucide-react';
import { callsAPI, flowsAPI } from '../../../api/client';
import { useTenant } from '../../../context/TenantContext';
import { apiError } from '../../../lib/host';
import { useBasic } from '../ui';
import Guide from './Guide';
import Inspector from './Inspector';
import { edgeColor, newNodeData, nodeTypes } from './nodes';
import Palette from './Palette';
import Simulator from './Simulator';
import { TEMPLATES } from './templates';
import './flows.css';

const SAVE_DELAY = 1000;
const EDITING_CHANGES = ['position', 'remove', 'add', 'replace'];
const NODE_W = 240;
const NODE_H = 130;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 2;

// Viewing preferences remembered per browser; storage can be unavailable (private mode), so failures are ignored.
function pref(key, fallback) {
  try {
    const v = localStorage.getItem(`tcx.flow.${key}`);
    return v === null ? fallback : v === 'true';
  } catch { return fallback; }
}
function savePref(key, value) {
  try { localStorage.setItem(`tcx.flow.${key}`, String(value)); } catch { /* not remembered: still works */ }
}

const isTyping = (el) => el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable);

/** − / zoom % / + / Fit / Map, always visible on the canvas. */
function ZoomBar({ showMap, onToggleMap }) {
  const { zoomIn, zoomOut, zoomTo, fitView } = useReactFlow();
  const { zoom } = useViewport();
  return (
    <Panel position="bottom-left" className="zoom-bar" aria-label="Zoom">
      <button type="button" onClick={() => zoomOut({ duration: 150 })} disabled={zoom <= MIN_ZOOM + 0.001} title="Zoom out (Ctrl −)" aria-label="Zoom out"><Minus size={15} /></button>
      <button type="button" className="zoom-level" onClick={() => zoomTo(1, { duration: 150 })} title="Reset to 100%">{Math.round(zoom * 100)}%</button>
      <button type="button" onClick={() => zoomIn({ duration: 150 })} disabled={zoom >= MAX_ZOOM - 0.001} title="Zoom in (Ctrl +)" aria-label="Zoom in"><Plus size={15} /></button>
      <span className="zoom-sep" />
      <button type="button" onClick={() => fitView({ padding: 0.15, duration: 250 })} title="Fit the whole flow on screen (Ctrl 0)"><Maximize size={14} /> Fit</button>
      <button type="button" className={showMap ? 'on' : ''} onClick={onToggleMap} aria-pressed={showMap} title="Show or hide the mini-map"><MapIcon size={14} /> Map</button>
    </Panel>
  );
}

function decorate(edge, nodes) {
  const source = nodes.find((n) => n.id === edge.source);
  const color = edgeColor(source?.type, edge.sourceHandle || 'output');
  return { ...edge, style: { stroke: color, strokeWidth: 2.5 }, markerEnd: { type: MarkerType.ArrowClosed, width: 18, height: 18, color } };
}

const plain = (nodes, edges) => ({
  nodes: nodes.map(({ id, type, position, data }) => ({ id, type, position, data })),
  edges: edges.map(({ id, source, sourceHandle, target, targetHandle }) => ({ id, source, sourceHandle: sourceHandle || 'output', target, targetHandle: targetHandle || 'input' })),
});

function Builder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tenant, can } = useTenant();
  const readOnly = !can('flows.write');
  // The dashboard's light / dark setting (the same switch as its top bar), also switchable here in full screen.
  const { dark = true, toggleTheme } = useBasic() || {};
  const theme = dark ? 'dark' : 'light';
  const { screenToFlowPosition, setCenter, getZoom, zoomIn, zoomOut, fitView } = useReactFlow();
  const [fullView, setFullView] = useState(() => pref('fullView', true));
  const [paletteOpen, setPaletteOpen] = useState(() => pref('paletteOpen', true));
  const [showMap, setShowMap] = useState(() => pref('showMap', false));
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [meta, setMeta] = useState(null);
  const [name, setName] = useState('');
  const [problems, setProblems] = useState({ errors: [], warnings: [] });
  const [selectedId, setSelectedId] = useState(null);
  const [simNodeId, setSimNodeId] = useState(null);
  const [context, setContext] = useState({ numbers: [], team: [] });
  const [saveState, setSaveState] = useState('saved'); // saved | pending | saving | failed
  const [toast, setToast] = useState(null);
  const [loadError, setLoadError] = useState('');
  const canvas = useRef(null);
  const fileInput = useRef(null);
  const latest = useRef({ nodes, edges, name });
  latest.current = { nodes, edges, name };

  const flash = useCallback((text, error = false) => {
    setToast({ text, error });
    setTimeout(() => setToast(null), error ? 6000 : 3000);
  }, []);

  useEffect(() => {
    flowsAPI.get(id).then((res) => {
      const { draft } = res.data;
      setMeta(res.data);
      setName(res.data.name);
      setNodes(draft.nodes || []);
      setEdges((draft.edges || []).map((e) => decorate(e, draft.nodes || [])));
      setProblems({ errors: res.data.errors, warnings: res.data.warnings });
    }).catch((err) => setLoadError(apiError(err, 'Could not open this call flow')));
    callsAPI.telephony().then((res) => setContext({ numbers: res.data.numbers, team: res.data.team })).catch(() => {});
  }, [id, setNodes, setEdges]);

  const saveNow = useCallback(async () => {
    if (readOnly) return true;
    setSaveState('saving');
    try {
      const { nodes: n, edges: e, name: nm } = latest.current;
      const res = await flowsAPI.save(id, { name: nm.trim() || 'Untitled flow', draft: plain(n, e) });
      setMeta((m) => ({ ...m, ...res.data }));
      setProblems({ errors: res.data.errors, warnings: res.data.warnings });
      setSaveState('saved');
      return true;
    } catch (err) {
      setSaveState('failed');
      flash(apiError(err, 'Could not save. Your changes are kept here; try again.'), true);
      return false;
    }
  }, [id, readOnly, flash]);

  // Drafts never affect real calls, so they save automatically shortly after each change.
  useEffect(() => {
    if (saveState !== 'pending') return undefined;
    const t = setTimeout(saveNow, SAVE_DELAY);
    return () => clearTimeout(t);
  }, [saveState, nodes, edges, name, saveNow]);

  const toggleFullView = useCallback(() => setFullView((v) => { savePref('fullView', !v); return !v; }), []);
  const togglePalette = () => setPaletteOpen((v) => { savePref('paletteOpen', !v); return !v; });
  const toggleMap = () => setShowMap((v) => { savePref('showMap', !v); return !v; });

  // Full screen covers the dashboard; the page underneath must not scroll.
  useEffect(() => {
    if (!fullView) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [fullView]);

  // Esc leaves full screen (unless a dialog is open); Ctrl +, Ctrl − and Ctrl 0 zoom the canvas, not the page.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape' && fullView && !document.querySelector('.guide-modal-overlay')) {
        toggleFullView();
        return;
      }
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); zoomIn({ duration: 150 }); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); zoomOut({ duration: 150 }); }
      else if (e.key === '0' && !isTyping(document.activeElement)) { e.preventDefault(); fitView({ padding: 0.15, duration: 250 }); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [fullView, toggleFullView, zoomIn, zoomOut, fitView]);

  useEffect(() => {
    const warn = (e) => { if (saveState !== 'saved') { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [saveState]);

  const changed = () => { if (!readOnly) setSaveState('pending'); };

  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    if (changes.some((c) => EDITING_CHANGES.includes(c.type))) changed();
    if (changes.some((c) => c.type === 'remove' && c.id === selectedId)) setSelectedId(null);
  }, [onNodesChange, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEdgesChange = useCallback((changes) => {
    onEdgesChange(changes);
    if (changes.some((c) => EDITING_CHANGES.includes(c.type))) changed();
  }, [onEdgesChange]); // eslint-disable-line react-hooks/exhaustive-deps

  const onConnect = useCallback((params) => {
    // One connection per exit: a new line from the same exit replaces the old one.
    setEdges((eds) => addEdge(
      decorate({ ...params, id: `e-${params.source}-${params.sourceHandle}-${params.target}` }, latest.current.nodes),
      eds.filter((e) => !(e.source === params.source && (e.sourceHandle || 'output') === (params.sourceHandle || 'output'))),
    ));
    changed();
  }, [setEdges]); // eslint-disable-line react-hooks/exhaustive-deps

  const addNode = useCallback((type, position) => {
    if (type === 'start' && latest.current.nodes.some((n) => n.type === 'start')) {
      flash('A flow can only have one "Call Comes In" block.', true);
      return;
    }
    const nodeId = `node-${Date.now()}`;
    setNodes((nds) => nds.concat({ id: nodeId, type, position, data: newNodeData(type) }));
    setSelectedId(nodeId);
    changed();
  }, [setNodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const onDrop = useCallback((event) => {
    event.preventDefault();
    const type = event.dataTransfer.getData('application/reactflow');
    if (type) addNode(type, screenToFlowPosition({ x: event.clientX, y: event.clientY }));
  }, [addNode, screenToFlowPosition]);

  // Blocks added from the palette go into free space: to the right of the selected block, or near the middle of
  // the view, stepping down (then further right) until nothing overlaps. The view then scrolls to show it.
  const freeSpot = (x, y) => {
    const gap = 30;
    const clash = (px, py) => latest.current.nodes.some((n) => {
      const w = n.measured?.width || NODE_W;
      const h = n.measured?.height || NODE_H;
      return px < n.position.x + w + gap && px + NODE_W + gap > n.position.x && py < n.position.y + h + gap && py + NODE_H + gap > n.position.y;
    });
    for (let col = 0; col < 6; col += 1) {
      for (let step = 0; step < 12; step += 1) {
        const dy = (step % 2 ? -1 : 1) * Math.ceil(step / 2) * (NODE_H + gap);
        const px = x + col * (NODE_W + 60);
        if (!clash(px, y + dy)) return { x: px, y: y + dy };
      }
    }
    return { x: x + 6 * (NODE_W + 60), y };
  };

  const addAtCentre = (type) => {
    const anchor = latest.current.nodes.find((n) => n.id === selectedId);
    let origin;
    if (anchor) {
      origin = { x: anchor.position.x + (anchor.measured?.width || NODE_W) + 90, y: anchor.position.y };
    } else {
      const box = canvas.current.getBoundingClientRect();
      const centre = screenToFlowPosition({ x: box.left + box.width / 2, y: box.top + box.height / 2 });
      origin = { x: centre.x - NODE_W / 2, y: centre.y - NODE_H / 2 };
    }
    const spot = freeSpot(Math.round(origin.x), Math.round(origin.y));
    addNode(type, spot);
    setCenter(spot.x + NODE_W / 2, spot.y + NODE_H / 2, { zoom: getZoom(), duration: 300 });
  };

  const updateNodeData = (nodeId, data) => {
    setNodes((nds) => nds.map((n) => (n.id === nodeId ? { ...n, data } : n)));
    if (data.options) {  // removing a menu key removes the line from that key
      const keys = new Set(data.options.map((o) => `key-${o.key}`));
      setEdges((eds) => eds.filter((e) => e.source !== nodeId || !e.sourceHandle?.startsWith('key-') || keys.has(e.sourceHandle)));
    }
    changed();
  };

  const deleteNode = (nodeId) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    changed();
  };

  const replaceGraph = (graph) => {
    setNodes(graph.nodes);
    setEdges(graph.edges.map((e) => decorate(e, graph.nodes)));
    setSelectedId(null);
    setSimNodeId(null);
    changed();
  };

  const loadTemplate = async (key) => {
    const tpl = TEMPLATES.find(([k]) => k === key);
    if (!tpl || !window.confirm(`Replace everything on the canvas with the "${tpl[1]}" template?`)) return;
    // Templates ring real team members, so make sure the team has loaded first.
    const team = context.team.length ? context.team : (await callsAPI.telephony()).data.team;
    replaceGraph(tpl[3](tenant.name, team));
  };

  const importJson = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        if (!Array.isArray(parsed.nodes)) throw new Error('no nodes');
        replaceGraph({ nodes: parsed.nodes, edges: Array.isArray(parsed.edges) ? parsed.edges : [] });
        flash('Flow imported. Check it, then publish when ready.');
      } catch {
        flash('That file is not a call flow exported from this builder.', true);
      }
    };
    reader.readAsText(file);
  };

  const exportJson = () => {
    const blob = new Blob([JSON.stringify(plain(nodes, edges), null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(name || 'call-flow').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const publish = async () => {
    if (!(await saveNow())) return;
    try {
      const res = await flowsAPI.publish(id);
      setMeta((m) => ({ ...m, ...res.data }));
      setProblems((p) => ({ ...p, warnings: res.data.warnings }));
      flash(res.data.replaced.length ? `Published. It replaces "${res.data.replaced.join('", "')}" on this number.` : 'Published. Real calls now follow this flow.');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail?.errors) {
        setProblems((p) => ({ ...p, errors: detail.errors }));
        flash('Fix the problems listed above, then publish again.', true);
      } else flash(apiError(err, 'Could not publish'), true);
    }
  };

  const unpublish = async () => {
    if (!window.confirm('Stop using this flow for real calls? Calls go back to standard routing (or another live flow).')) return;
    try {
      const res = await flowsAPI.unpublish(id);
      setMeta((m) => ({ ...m, ...res.data }));
      flash('Unpublished. Real calls no longer follow this flow.');
    } catch (err) { flash(apiError(err, 'Could not unpublish'), true); }
  };

  const displayNodes = useMemo(() => nodes.map((n) => (n.id === simNodeId
    ? { ...n, data: { ...n.data, isSimulated: true, isPlaying: n.type === 'playAudio' } } : n)), [nodes, simNodeId]);
  const selected = nodes.find((n) => n.id === selectedId) || null;

  if (loadError) return <div className={`tcx-flow ${theme}`}><div className="problems-bar"><span className="err">{loadError}</span></div></div>;
  if (!meta) return <div className={`tcx-flow ${theme}`}><div className="problems-bar">Loading call flow…</div></div>;

  const status = !meta.is_published ? ['draft', 'Draft'] : meta.has_unpublished_changes || saveState !== 'saved' ? ['changed', 'Live · changes not published'] : ['live', 'Live'];
  const saveLabel = { saved: 'All changes saved', pending: 'Unsaved changes…', saving: 'Saving…', failed: 'Save failed' }[saveState];

  return (
    <div className={`tcx-flow ${theme} ${fullView ? 'full' : ''}`} style={{ position: fullView ? 'fixed' : 'relative' }}>
      <header className="dashboard-header">
        <div className="logo-section">
          <button type="button" className="btn-header" onClick={() => navigate('/call-flows')} aria-label="Back to call flows"><ArrowLeft size={14} /></button>
          <div className="logo-icon-glow"><Play size={18} fill="#000" stroke="none" style={{ marginLeft: '2px' }} /></div>
          <input className="flow-name-input" aria-label="Flow name" value={name} disabled={readOnly} maxLength={150}
            onChange={(e) => { setName(e.target.value); changed(); }} />
          <span className={`flow-status ${status[0]}`}>{status[1]}</span>
          {!readOnly && <span className="flow-status draft" aria-live="polite">{saveLabel}</span>}
        </div>
        <div className="action-buttons-group">
          <Guide buttonClassName="btn-header" />
          {toggleTheme && (
            <button type="button" className="btn-header btn-theme" onClick={toggleTheme} aria-pressed={dark}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'} aria-label={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
              {dark ? <Sun size={14} /> : <Moon size={14} />}
            </button>
          )}
          <button type="button" className="btn-header" onClick={toggleFullView} aria-pressed={fullView}
            title={fullView ? 'Back to the dashboard view (Esc)' : 'Use the whole window for the builder'}>
            {fullView ? <><Minimize2 size={14} /> Exit Full Screen</> : <><Maximize2 size={14} /> Full Screen</>}
          </button>
          {!readOnly && (
            <select className="btn-header" value="" aria-label="Load template" onChange={(e) => loadTemplate(e.target.value)}>
              <option value="" disabled>Load Template…</option>
              {TEMPLATES.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
            </select>
          )}
          {!readOnly && <button type="button" className="btn-header" onClick={() => fileInput.current?.click()}><Upload size={14} /> Import JSON</button>}
          <input type="file" ref={fileInput} onChange={importJson} style={{ display: 'none' }} accept=".json,application/json" />
          <button type="button" className="btn-header" onClick={exportJson}><Download size={14} /> Export JSON</button>
          {!readOnly && (
            <button type="button" className="btn-header danger" onClick={() => window.confirm('Remove every block from the canvas?') && replaceGraph({ nodes: [], edges: [] })}>
              <Trash2 size={14} /> Clear Canvas
            </button>
          )}
          {!readOnly && meta.is_published && <button type="button" className="btn-header" onClick={unpublish}>Unpublish</button>}
          {!readOnly && <button type="button" className="btn-header primary" onClick={publish} disabled={saveState === 'saving'}><Rocket size={14} /> {meta.is_published ? 'Publish Changes' : 'Publish'}</button>}
        </div>
      </header>

      {readOnly && <div className="readonly-note">You can view this flow and test it in the simulator. Only the company owner can change or publish call flows.</div>}
      <div className={`problems-bar ${problems.errors.length || problems.warnings.length ? '' : 'ok'}`} role="status">
        {problems.errors.length === 0 && problems.warnings.length === 0 && <span>✓ Ready to publish: no problems found.</span>}
        {problems.errors.length > 0 && <><b className="err">Fix before publishing:</b><ul>{problems.errors.map((p) => <li key={p} className="err">{p}</li>)}</ul></>}
        {problems.warnings.length > 0 && <><b className="warn">Worth checking:</b><ul>{problems.warnings.map((p) => <li key={p} className="warn">{p}</li>)}</ul></>}
      </div>

      <div className="main-dashboard-body">
        <Palette onAdd={addAtCentre} readOnly={readOnly} collapsed={!paletteOpen} onToggle={togglePalette} />
        <main className="canvas-container" ref={canvas} onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }} onDrop={readOnly ? undefined : onDrop}>
          <ReactFlow
            nodes={displayNodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={handleEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={(_, node) => setSelectedId(node.id)}
            onPaneClick={() => setSelectedId(null)}
            nodesDraggable={!readOnly}
            nodesConnectable={!readOnly}
            deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
            minZoom={MIN_ZOOM}
            maxZoom={MAX_ZOOM}
            fitView
            fitViewOptions={{ padding: 0.15 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color={dark ? '#2e303a' : '#c7d0dd'} gap={20} size={1} />
            <ZoomBar showMap={showMap} onToggleMap={toggleMap} />
            {showMap && (
              <MiniMap position="top-right" pannable zoomable style={{ width: 170, height: 110, background: 'var(--bg-dark)', border: '1px solid var(--border-glow)', borderRadius: '10px' }}
                nodeColor={() => 'var(--border-focus)'} maskColor={dark ? 'rgba(0, 0, 0, 0.45)' : 'rgba(15, 23, 42, 0.08)'} />
            )}
          </ReactFlow>
        </main>
        {/* Only shown for a selected block, so the canvas gets the space otherwise. */}
        {selected && (
          <Inspector node={selected} onChange={updateNodeData} onDelete={deleteNode} onClose={() => setSelectedId(null)}
            readOnly={readOnly} numbers={context.numbers} team={context.team} />
        )}
        <Simulator nodes={nodes} edges={edges} team={context.team} activeNodeId={simNodeId} setActiveNodeId={setSimNodeId} />
      </div>
      {toast && <div className={`toast ${toast.error ? 'error' : ''}`} role="alert">{toast.text}</div>}
    </div>
  );
}

export default function FlowBuilderPage() {
  return <ReactFlowProvider><Builder /></ReactFlowProvider>;
}
